// =============================================================================
// MeuDriver — Cartão "Lista de espera" no dashboard do instrutor
// -----------------------------------------------------------------------------
// Mostra alunos aguardando vaga + botão "Notificar disponibilidade".
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Clock, Bell, Loader2 } from 'lucide-react'
import { formatDate, getInitials } from '@/lib/utils'
import type { ProfileRow } from '@/types/database'

interface WaitlistEntry {
  id: string
  student_id: string
  created_at: string
  student: Pick<ProfileRow, 'id' | 'full_name' | 'avatar_url' | 'city'> | null
}

export function InstructorWaitlistCard({ instructorId }: { instructorId: string }) {
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [notifying, setNotifying] = useState(false)

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('instructor_waitlist')
      .select(`
        id, student_id, created_at,
        student:profiles!instructor_waitlist_student_id_fkey(id, full_name, avatar_url, city)
      `)
      .eq('instructor_id', instructorId)
      .order('created_at', { ascending: true })
    setEntries((data ?? []) as unknown as WaitlistEntry[])
    setLoading(false)
  }

  useEffect(() => {
    if (instructorId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId])

  async function notifyAvailability() {
    if (entries.length === 0) return
    setNotifying(true)
    try {
      // INSERT direto em `notifications` é bloqueado por RLS (somente
      // service_role pode inserir). Usamos a RPC `notify_waitlist`, que roda
      // como SECURITY DEFINER, valida posse do instrutor e aplica rate limit
      // de 1 disparo por hora.
      const { data, error } = await supabase.rpc('notify_waitlist', {
        p_instructor_id: instructorId,
      })
      if (error) throw error
      toast.success(`${data as number} aluno(s) notificado(s)!`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao notificar lista.')
    } finally {
      setNotifying(false)
    }
  }

  async function removeEntry(id: string) {
    if (!confirm('Remover este aluno da sua lista de espera?')) return
    const { error } = await supabase.from('instructor_waitlist').delete().eq('id', id)
    if (error) {
      toast.error(error.message)
      return
    }
    await load()
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-600" />
          Lista de espera
          {entries.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
              {entries.length}
            </span>
          )}
        </CardTitle>
        {entries.length > 0 && (
          <Button
            size="sm"
            onClick={notifyAvailability}
            disabled={notifying}
            className="gap-1 bg-amber-600 hover:bg-amber-700"
          >
            {notifying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Bell className="h-3.5 w-3.5" />
            )}
            Notificar disponibilidade
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Carregando...</div>
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Ninguém na sua lista de espera.
          </div>
        ) : (
          <ul className="divide-y">
            {entries.map((e) => (
              <li key={e.id} className="px-4 py-3 flex items-center gap-3">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={e.student?.avatar_url ?? undefined} />
                  <AvatarFallback className="text-xs">
                    {getInitials(e.student?.full_name ?? 'A')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {e.student?.full_name ?? 'Aluno'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {e.student?.city ?? 'Cidade não informada'} · entrou em {formatDate(e.created_at)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEntry(e.id)}
                  className="text-xs text-muted-foreground hover:text-red-600"
                >
                  Remover
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
